/**
 * The business day as the Console shows it (IP-134, IP-135; design
 * docs/superpowers/specs/2026-10-06-order-review-business-day-design.md
 * section 8.1) - pure, and free of React, so node --test covers every rule
 * (businessDay.test.js). BusinessDayProvider.jsx reads GET /books/days
 * (@lfoc/domain/business-day/reads.js) once for the whole Console; the topbar
 * chip (BusinessDayChip.jsx) and its drawer (BusinessDayDrawer.jsx) render
 * what these helpers decide:
 *
 *   chipOf          which day the chip names - the oldest that is not CLOSED,
 *                   else the latest CLOSED one, else the open dealing day the
 *                   calendar gives (a day's row is never made ahead of time,
 *                   design 5.2) - its words, its dot and its tooltip
 *   pollGate        whether the provider may read now: only while the person
 *                   was active within the last heartbeat window, never while
 *                   the page is hidden or the idle warning shows - the
 *                   authorizer counts every call as activity, so a poll that
 *                   ignored the idle rule would keep an idle session alive
 *   pollDelay       the next read's delay: the heartbeat's minute, doubled
 *                   after each failure up to five minutes
 *   stepItems       the drawer's Steps in order (brief B-4 as amended on 6
 *                   Oct 2026: reconciliation follows repricing), a stage the
 *                   day moved past in one move marked "passed over"
 *   checklistRows   the day's checklist, whatever its stage: D's statement
 *                   arrives on D+1's morning and its funding window runs to
 *                   D+1's cut-off, so the statements and the funding window
 *                   show from the cut-off on, though only RECONCILING (after
 *                   repricing) waits for them before day end; a row a force
 *                   passed is marked forced, never left "waiting"
 *   statementCell   a fund's statement per COLLECTION account - Loaded only
 *                   when every one is in - the missing accounts named
 *                   (accountLabel, accountNames: bank and number)
 *   forceOptions    what DAY_ADVANCE's force would pass now (design 5.5:
 *                   missing prices; statements and the funding window) - and
 *                   never pending orders
 *   advanceOutcome  what an advance did, in words
 *   advanceShown    whether Advance is offered: on a day the Day end process
 *                   owns (IP-200), only up to Reconciliation & approvals -
 *                   from there Run day end closes the day
 *   runDayEndState  the Run day end button: shown, its label, and why it is
 *                   held (a run going, an earlier day open)
 *   dayEndPanel     a Day end day's status from Reconciliation on: the run,
 *                   who started it, its progress, what to do
 *   hiddenDaysText  the days not CLOSED the read did not send: it sends the
 *                   five oldest and every one from today (liveDays counts
 *                   them all), so a backlog never hides today, but the days
 *                   between are said, never silently missing
 *
 * A day the Day end process owns (IP-200, GET's dayEnd set) waits for
 * neither its statements nor its funding window, nor its transactions still
 * waiting for approval (Run day end carries them over or moves them): a
 * missing statement and a pending transaction are warnings (`warn`, never
 * "waiting"), the funding window is not listed, and - given the view -
 * "Earlier Days Closed" says whether an earlier one must close first.
 *
 * The stage names and labels are the domain's own (business-day/stages.js,
 * browser-safe), and the server's `stages` list wins when it sends one. In
 * the Console this is businessDay, never stage: principal.stage is the
 * deployment environment. Times are Colombo wall-clock (Rule 36(3)).
 */
import { VIEW_CLAIMS } from '@lfoc/domain/authority/claims.js';
import { DAY_STEP, STAGE, STAGES } from '@lfoc/domain/business-day/stages.js';

import { HEARTBEAT_MS, IDLE_MS, WARN_MS } from '../auth/idle.js';
import { formatInstant } from '../format.js';

/** The longest a failing read waits before the next try. */
export const MAX_BACKOFF_MS = 5 * 60 * 1000;

/** DAY_ADVANCE's reason (catalogue.js DAY_ADVANCE): 3 to 500 characters, one line. */
export const REASON_MIN = 3;
export const REASON_MAX = 500;

/** The chip's dot: white while the day is open for orders, gold while it is processed, green once closed. */
export const TONE = Object.freeze({ OPEN: 'open', PROCESSING: 'processing', CLOSED: 'closed', UNKNOWN: 'unknown' });

/**
 * The steps that run over time: the chip's gold dot pulses while one runs. Repricing pauses the maker's
 * review (IP-129); day end no longer does (IP-171), but it still runs over time.
 */
const RUNNING = new Set([STAGE.REPRICING, STAGE.DAY_END]);

/** The day's instant of reaching each stage (business-day/day.js mapDay). */
const AT_FIELD = Object.freeze({
  OPEN: 'openedAt', FROZEN: 'frozenAt', PRICED: 'pricedAt', REPRICING: 'repricingAt',
  RECONCILING: 'reconcilingAt', DAY_END: 'dayEndAt', CLOSED: 'closedAt',
});

/**
 * Stages migration 0021 retired, as a day's trail from before it still names
 * them (business_day_event is append-only): 0019's REPRICED, between the
 * Repricing step and day end.
 */
const RETIRED_LABEL = Object.freeze({ REPRICED: 'Priced again · approvals' });

/** Each move's reason (business_day_event.reason, stages.js MOVE_REASON) as the timeline says it. */
export const REASON_TEXT = Object.freeze({
  CREATED: 'Day started',
  BOOKS_CLOSED: 'Books closed at the cut-off',
  STATEMENT_LOADED: 'Bank statement loaded',
  PRICES_PUBLISHED: 'Prices published',
  PRICE_FORCED: 'Moved past missing prices (forced)',
  REPRICING_STARTED: 'Transaction pricing started',
  REPRICING_FINISHED: 'Transaction pricing finished',
  DAY_END_STARTED: 'Day end started',
  DAY_END_FORCED: 'Day end started (forced)',
  DAY_END_WRITTEN: 'Day end summary written',
});

/** advanceBusinessDays's failed steps, keyed as the server sends them (stages.js DAY_STEP), as an outcome names them. */
const STEP_TEXT = Object.freeze({
  [DAY_STEP.READ]: 'Reading the live days',
  [DAY_STEP.TODAY]: 'Starting the day',
  [DAY_STEP.SYNC]: 'Reading the day\'s facts',
  [DAY_STEP.REPRICING]: 'Transaction pricing',
  [DAY_STEP.DAY_END]: 'Day end',
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
// One line: no C0 control, DEL, NEL or line/paragraph separator - the catalogue's own rule for a plain text.
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f\u0085\u2028\u2029]/;

const TIME = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Colombo', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const DAY_PARTS = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Colombo', year: 'numeric', month: '2-digit', day: '2-digit' });

const plural = (n, one, many = one + 's') => n + ' ' + (n === 1 ? one : many);

function parts(date) {
  const m = DATE.exec(String(date ?? ''));
  return m ? { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) } : null;
}

/** '2026-10-06' -> '6 Oct'; anything else as it is. */
export function dayShort(date) {
  const p = parts(date);
  return p ? p.day + ' ' + MONTHS[p.month - 1] : String(date ?? '');
}

/** '2026-10-06' -> '6 Oct 2026' (the server's own sentence form, business-day/pause.js dayText). */
export function dayText(date) {
  const p = parts(date);
  return p ? p.day + ' ' + MONTHS[p.month - 1] + ' ' + p.year : String(date ?? '');
}

/** '2026-10-06' -> 'Tue 6 Oct 2026': a calendar date, so its weekday is the date's own, in no time zone. */
export function dayLong(date) {
  const p = parts(date);
  return p ? WEEKDAYS[new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay()] + ' ' + dayText(date) : String(date ?? '');
}

/** An instant's Colombo date, YYYY-MM-DD. */
export function colomboDate(iso) {
  const got = Object.fromEntries(DAY_PARTS.formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
  return got.year + '-' + got.month + '-' + got.day;
}

/** An instant as Colombo HH:mm. */
export const timeText = (iso) => TIME.format(new Date(iso));

/** An instant beside a business date: '14:10' on the date itself, '7 Oct, 14:00' on another; '—' for none. */
export function whenText(iso, businessDate = null) {
  if (!iso) return '—';
  const date = colomboDate(iso);
  return date === businessDate ? timeText(iso) : dayShort(date) + ', ' + timeText(iso);
}

/** The stages in order: the server's list when it sent one, else the domain's. */
export function stagesOf(view) {
  const sent = view?.stages;
  return Array.isArray(sent) && sent.length ? sent : STAGES.map((s) => ({ stage: s.stage, label: s.label, pausesReview: s.pausesReview }));
}

/**
 * A stage's label ('Cut-off · frozen'); a retired one's as it was (an old
 * day's trail), or the name itself for one this build does not know.
 */
export function stageLabel(stage, stages = stagesOf(null)) {
  return stages.find((s) => s.stage === stage)?.label ?? RETIRED_LABEL[stage] ?? String(stage ?? '');
}

/**
 * The chip's short form of a label: its first part ('Cut-off · frozen' ->
 * 'Cut-off', 'Reconciliation & approvals' -> 'Reconciliation').
 */
export const shortLabel = (stage, stages) => stageLabel(stage, stages).split(/ · | & /)[0];

/** A stage's place in the order, 1 (OPEN) to 7 (CLOSED); 0 for one not in the list. */
export const rankIn = (stages, stage) => stages.findIndex((s) => s.stage === stage) + 1;

/** A stage's tone: the chip's dot and the drawer's tag. */
export function toneOf(stage) {
  if (!stage) return TONE.UNKNOWN;
  if (stage === STAGE.OPEN) return TONE.OPEN;
  if (stage === STAGE.CLOSED) return TONE.CLOSED;
  return TONE.PROCESSING;
}

const byDate = (a, b) => (a.businessDate < b.businessDate ? -1 : a.businessDate > b.businessDate ? 1 : 0);

/** The live days (not CLOSED), oldest first: the first is the Console's "current stage" (design 5.2, "Two live days"). */
export const liveDays = (view) => (view?.days ?? []).filter((d) => d.stage !== STAGE.CLOSED).sort(byDate);

/** The day the chip names: the oldest live one, else the latest CLOSED one, else none. */
export function shownDay(view) {
  const live = liveDays(view);
  if (live.length) return live[0];
  const closed = (view?.days ?? []).filter((d) => d.stage === STAGE.CLOSED).sort(byDate);
  return closed.length ? closed[closed.length - 1] : null;
}

/** The days as the drawer's tabs list them: the live ones oldest first, then the CLOSED one(s), newest first. */
export function dayTabs(view) {
  const closed = (view?.days ?? []).filter((d) => d.stage === STAGE.CLOSED).sort(byDate).reverse();
  const stages = stagesOf(view);
  return [...liveDays(view), ...closed].map((day) => ({
    key: day.businessDate,
    day,
    label: dayShort(day.businessDate) + ' · ' + shortLabel(day.stage, stages),
  }));
}

/**
 * The days not CLOSED that GET /books/days did not send (its liveDays counts
 * every one, its days the five oldest and every one from today), in a
 * sentence; null when every one has its tab, or the server does not count.
 */
export function hiddenDaysText(view) {
  const counted = view?.liveDays;
  if (typeof counted !== 'number') return null;
  const hidden = counted - liveDays(view).length;
  if (hidden <= 0) return null;
  return hidden === 1
    ? '1 more day is not closed and not shown here: it comes into view as the oldest days close.'
    : hidden + ' more days are not closed and not shown here: they come into view as the oldest days close.';
}

/** The pause, in the server's own words (business-day/pause.js pauseRefusal); null when nothing is paused. */
export function pauseText(reviewPaused) {
  if (!reviewPaused?.paused) return null;
  return 'Reviews are paused while the business day of ' + dayText(reviewPaused.businessDate) + ' is '
    + (reviewPaused.stageLabel ?? reviewPaused.stage) + '. They reopen when it finishes.';
}

/** The open dealing day (the first business day whose cut-off is ahead), in a sentence; null when the calendar gives none. */
export function openDayText(view) {
  const open = view?.openDealingDay;
  if (!open?.businessDate) return null;
  return 'Open for transactions: ' + dayLong(open.businessDate) + ', until its cut-off at ' + whenText(open.cutoffAt, open.businessDate) + '.';
}

/**
 * The topbar chip (design 8.1): the oldest live day and its stage, '6 Oct ·
 * Repricing', with a dot - white while open, gold while the day is processed
 * (pulsing while Repricing or day end runs), green once closed. Before
 * any day exists it names the open dealing day; before the first read, or
 * with nothing to name, it reads "Business Day". Its tooltip says what the
 * drawer says in brief, "Reviews paused" during a pause.
 *
 * @param {object|null} view GET /books/days's answer
 * @param {{ error?: { message: string }|null }} [options] the last read's failure, if it failed
 * @returns {{ businessDate: string|null, stage: string|null, date: string|null, label: string, tone: string,
 *   running: boolean, paused: boolean, stale: boolean, tooltip: string[], ariaLabel: string }}
 */
export function chipOf(view, { error = null } = {}) {
  const stages = stagesOf(view);
  const day = shownDay(view);
  const open = view?.openDealingDay?.businessDate ? view.openDealingDay : null;
  const paused = pauseText(view?.reviewPaused);
  const businessDate = day?.businessDate ?? open?.businessDate ?? null;
  const stage = day ? day.stage : open ? STAGE.OPEN : null;

  const tooltip = [];
  if (stage) {
    tooltip.push('Business day ' + dayText(businessDate) + ': ' + stageLabel(stage, stages)
      + (day?.stageEnteredAt ? ', since ' + whenText(day.stageEnteredAt, businessDate) : ''));
  } else {
    tooltip.push(view ? 'No business day yet.' : 'Reading the business day…');
  }
  if (paused) tooltip.push('Reviews paused. ' + paused);
  const others = liveDays(view).filter((d) => d.businessDate !== businessDate);
  if (others.length) tooltip.push('Also in progress: ' + others.map((d) => dayShort(d.businessDate) + ' · ' + shortLabel(d.stage, stages)).join(', ') + '.');
  if (open && open.businessDate !== businessDate) tooltip.push(openDayText(view));
  if (error) tooltip.push('Not refreshed: ' + error.message);

  const label = stage ? shortLabel(stage, stages) : 'Business Day';
  const date = businessDate ? dayShort(businessDate) : null;
  return {
    businessDate,
    stage,
    date,
    label,
    tone: toneOf(stage),
    running: RUNNING.has(stage),
    paused: Boolean(paused),
    stale: Boolean(error),
    tooltip,
    ariaLabel: 'Business day' + (date ? ': ' + date + ', ' + stageLabel(stage, stages) : '') + (paused ? '. Reviews paused' : '') + '. Open the business day.',
  };
}

/**
 * Whether the provider may read GET /books/days now (design 8.1, "Polling
 * respects the server's idle rule"). The authorizer counts every call as
 * activity, so a read is made only while the person was active within the
 * last heartbeat window (auth/idle.js HEARTBEAT_MS) - the window in which the
 * idle monitor's own heartbeat may already have reached the server - never
 * once the idle warning shows (idleMs - warnMs without activity), and never
 * while the page is hidden.
 *
 * @param {{ nowMs: number, lastActivityMs: number, hidden?: boolean, heartbeatMs?: number, idleMs?: number, warnMs?: number }} at
 * @returns {{ open: boolean, reason: 'HIDDEN'|'WARNING'|'IDLE'|null }}
 */
export function pollGate({ nowMs, lastActivityMs, hidden = false, heartbeatMs = HEARTBEAT_MS, idleMs = IDLE_MS, warnMs = WARN_MS }) {
  if (hidden) return { open: false, reason: 'HIDDEN' };
  const quiet = nowMs - lastActivityMs;
  if (quiet >= idleMs - warnMs) return { open: false, reason: 'WARNING' };
  if (quiet > heartbeatMs) return { open: false, reason: 'IDLE' };
  return { open: true, reason: null };
}

/** The next read's delay after `failures` failed reads in a row: the heartbeat's cadence, doubling up to maxMs. */
export function pollDelay(failures, { heartbeatMs = HEARTBEAT_MS, maxMs = MAX_BACKOFF_MS } = {}) {
  if (!(failures > 0)) return heartbeatMs;
  return Math.min(maxMs, heartbeatMs * 2 ** Math.min(failures, 16));
}

/**
 * The drawer's Steps for one day, in order: a stage reached is finished, the
 * day's own stage in progress (finished once CLOSED), a later one waiting. A
 * stage a single move went past (business_day_event.skipped - FROZEN when the
 * price was in before the day was synced past its cut-off; RECONCILING on a
 * day from before 0021, when the price came before its statement) is marked
 * passed over, and a forced one (business_day.forced) forced.
 *
 * @param {object} day one of GET /books/days's days
 * @param {Array<{ stage: string, label: string }>} [stages]
 * @returns {Array<{ key: string, stage: string, title: string, status: 'finish'|'process'|'wait', passedOver: boolean,
 *   forced: boolean, at: string|null, content: string|null }>}
 */
export function stepItems(day, stages = stagesOf(null)) {
  const passed = new Set((day?.events ?? []).flatMap((e) => e.skipped ?? []));
  const forced = new Set((day?.forced ?? []).map((f) => f.stage));
  const current = rankIn(stages, day?.stage);
  return stages.map((s, i) => {
    const rank = i + 1;
    const reached = rank <= current;
    const passedOver = reached && passed.has(s.stage);
    const at = reached ? day?.[AT_FIELD[s.stage]] ?? null : null;
    let status = 'wait';
    if (rank < current || (rank === current && day.stage === STAGE.CLOSED)) status = 'finish';
    else if (rank === current) status = 'process';
    if (passedOver) status = 'wait';
    const isForced = reached && forced.has(s.stage);
    let content = null;
    if (passedOver) content = 'Passed over';
    else if (at) content = (isForced ? 'Forced · ' : '') + whenText(at, day.businessDate);
    return { key: s.stage, stage: s.stage, title: s.label, status, passedOver, forced: isForced, at, content };
  });
}

/**
 * The checklist rows a force passed (business_day.forced[].missing): the
 * prices a force to PRICED passed, the statements and the funding window a
 * force to DAY_END passed - each with the stage it was forced at.
 *
 * @returns {Map<string, string>} checklist row key -> the stage's label
 */
function forcedRows(day, stages) {
  const rows = new Map();
  for (const f of day?.forced ?? []) {
    const label = stageLabel(f.stage, stages);
    const missing = f.missing ?? {};
    if (missing.fundsMissingPrice?.length) rows.set('prices', label);
    if (missing.statementsMissing?.length) rows.set('statements', label);
    if (missing.fundingWindowClosesAt !== undefined) rows.set('fundingWindow', label);
  }
  return rows;
}

/**
 * The day's checklist (design 8.1): books closed, statements loaded n/m, the
 * funding window, funds missing a price, pending orders - each done or not,
 * with a line of text, from the day's own facts whatever its stage. A row
 * not done that a force passed is `forced`, its text saying where - so a day
 * forced on, closed now, does not read as still waiting for it.
 *
 * @param {object} day one of GET /books/days's days
 * @param {{ nowMs: number, stages?: object[], view?: object|null }} at the read's instant (the view's `now`), and the view
 * @returns {Array<{ key: string, label: string, done: boolean, forced: boolean, warn: boolean, text: string }>}
 */
export function checklistRows(day, { nowMs, stages = stagesOf(null), view = null }) {
  const c = day?.checklist ?? {};
  const loaded = Number(c.statements?.loaded ?? 0);
  const expected = Number(c.statements?.expected ?? 0);
  const closesAt = c.fundingWindowClosesAt ?? null;
  const windowClosed = closesAt !== null && nowMs >= Date.parse(closesAt);
  const missing = c.fundsMissingPrice ?? [];
  const pending = Number(c.pendingOrders ?? 0);
  const cutoffPassed = day?.cutoffAt ? nowMs >= Date.parse(day.cutoffAt) : false;

  let books;
  if (c.booksClosed && cutoffPassed) books = 'Every book of the day is closed';
  else if (c.booksClosed) books = 'Taking transactions until the cut-off at ' + whenText(day.cutoffAt, day.businessDate);
  else if (cutoffPassed) books = 'Cut-off ' + whenText(day.cutoffAt, day.businessDate) + ' passed; a book is still open';
  else books = 'Open until the cut-off at ' + whenText(day?.cutoffAt, day?.businessDate);

  let window;
  if (closesAt === null) window = 'Closes at the next business day\'s cut-off, which the calendar does not hold yet';
  else window = (windowClosed ? 'Closed at ' : 'Open until ') + whenText(closesAt, day.businessDate);

  const forced = forcedRows(day, stages);
  const ownedByDayEnd = isDayEndDay(day);
  const statementsText = expected ? loaded + ' of ' + expected : 'No collection account expects one';
  const pendingText = pending ? plural(pending, 'transaction') + ' to approve or reject' : 'None';
  const rows = [
    { key: 'books', label: 'Books Closed', done: Boolean(c.booksClosed) && cutoffPassed, text: books },
    ownedByDayEnd && loaded < expected
      ? { key: 'statements', label: 'Statements Loaded', done: false, warn: true, text: statementsText + ' · a warning: Day end can still run' }
      : { key: 'statements', label: 'Statements Loaded', done: loaded >= expected, text: statementsText },
    ownedByDayEnd ? null : { key: 'fundingWindow', label: 'Funding Window', done: windowClosed, text: window },
    { key: 'prices', label: 'Funds Missing a Price', done: missing.length === 0, text: missing.length ? missing.join(', ') : 'None' },
    ownedByDayEnd && pending
      ? { key: 'pending', label: 'Pending Transactions', done: false, warn: true,
        text: pendingText + ' · Run day end carries over the reconciled ones and moves the rest' }
      : { key: 'pending', label: 'Pending Transactions', done: pending === 0, text: pendingText },
  ];
  if (ownedByDayEnd && view && day.stage !== STAGE.CLOSED) {
    const earlier = earlierOpenDay(view, day);
    rows.push({ key: 'earlier', label: 'Earlier Days Closed', done: !earlier, text: earlier ? 'Close ' + dayShort(earlier) + ' first' : 'Yes' });
  }
  return rows.filter(Boolean).map((row) => {
    const at = row.done ? null : forced.get(row.key);
    if (at) return { ...row, forced: true, warn: false, text: row.text + ' · forced at ' + at };
    return { ...row, forced: false, warn: row.warn === true };
  });
}

/** An account as people know it: its bank and number ('DEUTSCHE BANK 0046524004'), else its id's first part. */
export function accountLabel(account) {
  const named = [account?.bankName, account?.accountNumber].filter(Boolean).join(' ');
  return named || String(account?.accountId ?? '').split('-')[0];
}

/**
 * The day's COLLECTION accounts by id, each as '<fund> <bank> <number>': from
 * its funds (GET's funds[].accounts) and its summary's statements (day end
 * keeps them - a closed day's table may list none). Accounts neither names
 * with a bank are left out.
 *
 * @returns {Map<string, string>}
 */
export function accountNames(day) {
  const names = new Map();
  const add = (fundId, account) => {
    if (!account?.accountId || !(account.bankName || account.accountNumber)) return;
    names.set(String(account.accountId).toLowerCase(), fundId + ' ' + accountLabel(account));
  };
  for (const s of day?.summary?.statements ?? []) add(s.fundId, s);
  for (const f of day?.funds ?? []) for (const a of f.accounts ?? []) add(f.fundId, a);
  return names;
}

/**
 * A fund's statement in the day's table: Loaded only when every COLLECTION
 * account's is in, else 'n of m loaded' or 'Not loaded', with the accounts
 * still missing named. An answer from before statements were per account has
 * statementLoaded (any one loaded) only.
 *
 * @returns {{ text: string, missing: string[] }}
 */
export function statementCell(fund) {
  if (!fund?.statements) return { text: fund?.statementLoaded ? 'Loaded' : 'Not loaded', missing: [] };
  const { loaded, expected } = fund.statements;
  const missing = (fund.accounts ?? []).filter((a) => !a.loaded).map(accountLabel);
  if (!expected) return { text: 'No collection account', missing };
  if (loaded >= expected) return { text: 'Loaded', missing };
  return { text: loaded ? loaded + ' of ' + expected + ' loaded' : 'Not loaded', missing };
}

/**
 * The Day end process's own figures on a day it closed (summary.dayEnd, from
 * day-end/day.js closeDay: the run's attempt, the holders it checked and the
 * ones it rebuilt), in a line; null for a day closed without it.
 */
export function dayEndCheckText(summary) {
  const d = summary?.dayEnd;
  if (!d) return null;
  const checked = Number(d.holdersChecked ?? 0);
  return 'Day end check: ' + checked.toLocaleString('en-US') + ' holder' + (checked === 1 ? '' : 's') + ' checked, '
    + Number(d.rebuilt ?? 0).toLocaleString('en-US') + ' rebuilt' + (Number(d.attempt) > 1 ? ' (attempt ' + d.attempt + ')' : '');
}

/**
 * What DAY_ADVANCE's force would pass now (design 5.5) - the page offers
 * Force only when it would pass something: funds still missing a price once
 * the books are closed at the cut-off (FROZEN's facts are never forced), and
 * statements not loaded or the funding window still open, for a day not yet
 * at day end with no order pending. Pending orders are never forced past:
 * they are approved or rejected. The server decides again (business-day/
 * stages.js forcePriced, decideDayEnd).
 *
 * @param {object} day one of GET /books/days's days
 * @param {{ nowMs: number, stages?: object[] }} at
 * @returns {{ items: string[], blockedByPending: boolean }}
 */
export function forceOptions(day, { nowMs, stages = stagesOf(null) }) {
  const c = day?.checklist ?? {};
  const rank = rankIn(stages, day?.stage);
  const priced = rankIn(stages, STAGE.PRICED);
  const dayEnd = rankIn(stages, STAGE.DAY_END);
  const pending = Number(c.pendingOrders ?? 0);
  const frozen = Boolean(c.booksClosed) && Boolean(day?.cutoffAt) && nowMs >= Date.parse(day.cutoffAt);
  const items = [];
  const missing = c.fundsMissingPrice ?? [];
  if (rank > 0 && rank < priced && frozen && missing.length) items.push('Funds without a price: ' + missing.join(', '));
  // IP-200: a day the Day end process owns is offered only the missing prices - Day end waits for
  // neither its statements nor its funding window, and Run day end decides its pending transactions.
  if (rank > 0 && rank < dayEnd && frozen && pending === 0 && !isDayEndDay(day)) {
    const closesAt = c.fundingWindowClosesAt ?? null;
    if (closesAt === null || nowMs < Date.parse(closesAt)) {
      items.push(closesAt ? 'The funding window, open until ' + whenText(closesAt, day.businessDate) : 'The funding window, still open');
    }
    const loaded = Number(c.statements?.loaded ?? 0);
    const expected = Number(c.statements?.expected ?? 0);
    if (loaded < expected) items.push(plural(expected - loaded, 'bank statement') + ' not loaded');
  }
  return { items, blockedByPending: rank > 0 && rank < dayEnd && pending > 0 && !isDayEndDay(day) };
}

/** Why a force's reason is refused, or null: 3 to 500 characters, trimmed, on one line (the catalogue's DAY_ADVANCE rule). */
export function reasonProblem(value) {
  const trimmed = String(value ?? '').trim();
  if (trimmed.length < REASON_MIN) return 'Give a reason of at least ' + REASON_MIN + ' characters.';
  if (trimmed.length > REASON_MAX) return 'Keep the reason to ' + REASON_MAX + ' characters.';
  if (CONTROL.test(trimmed)) return 'Keep the reason on one line, with no line breaks, tabs or other control characters.';
  return null;
}

/** DAY_ADVANCE's body (the day is in the path): a force carries its reason, trimmed; a plain advance carries none. */
export function advanceBody({ force = false, reason = '' } = {}) {
  return force ? { force: true, reason: String(reason ?? '').trim() } : { force: false };
}

/**
 * The statements a move or a blocker was missing, counted - and named by
 * account (accountNames) when every one of them can be.
 */
function statementsMissingText(ids, names) {
  const known = ids.map((id) => names?.get(String(id).toLowerCase()));
  const text = plural(ids.length, 'bank statement') + ' not loaded';
  return known.every(Boolean) ? text + ': ' + known.join(', ') : text;
}

/**
 * What a move or a blocker was missing, in words (business_day.forced[].missing, decideDayEnd's missing).
 *
 * @param {Map<string, string>|null} [names] accountNames of the day, to name the statements missing
 */
export function missingText(missing, businessDate = null, names = null) {
  if (!missing) return [];
  const lines = [];
  if (missing.fundsMissingPrice?.length) lines.push('prices for ' + missing.fundsMissingPrice.join(', '));
  if (missing.fundingWindowClosesAt !== undefined) {
    lines.push(missing.fundingWindowClosesAt ? 'the funding window, open until ' + whenText(missing.fundingWindowClosesAt, businessDate) : 'the funding window');
  }
  if (missing.statementsMissing?.length) lines.push(statementsMissingText(missing.statementsMissing, names));
  return lines;
}

/**
 * What a DAY_ADVANCE did (business-day/action.js's answer: the day as GET
 * shows it, with advance { from, to, moves, blockedBy?, missing?,
 * repricing?, failures, complete }), as an Alert says it - repricing.busy
 * when another pass held the Repricing step (BD-4).
 *
 * @returns {{ type: 'success'|'info'|'warning'|'error', title: string, lines: string[] }}
 */
export function advanceOutcome(answer, stages = stagesOf(answer)) {
  const a = answer?.advance ?? {};
  const moves = a.moves ?? [];
  const failures = a.failures ?? [];
  const label = (stage) => stageLabel(stage, stages);
  // The answer is the day as GET shows it: its funds name its accounts.
  const names = accountNames(answer);
  const lines = [];

  for (const move of moves) {
    if (move.skipped?.length) lines.push('Passed over ' + move.skipped.map(label).join(', ') + '.');
    if (move.forced) lines.push('Forced to ' + label(move.forced.stage) + ' past ' + missingText(move.forced.missing, a.businessDate, names).join('; ') + '.');
  }
  if (a.blockedBy?.length) {
    const m = a.missing ?? {};
    const waits = a.blockedBy.map((b) => {
      if (b === 'PENDING_ORDERS') return plural(Number(m.pendingOrders ?? 0), 'pending transaction') + ' to approve or reject';
      if (b === 'FUNDING_WINDOW_OPEN') return m.fundingWindowClosesAt ? 'the funding window, open until ' + whenText(m.fundingWindowClosesAt, a.businessDate) : 'the funding window';
      if (b === 'STATEMENTS_MISSING') return statementsMissingText(m.statementsMissing ?? [], names);
      return b;
    });
    lines.push('Day end is waiting for ' + waits.join('; ') + '.');
  }
  for (const f of failures) lines.push((STEP_TEXT[f.step] ?? f.step) + ' failed: ' + (f.error ?? 'no reason given') + '.');
  if (a.repricing?.busy) lines.push('Another pass is running this day\'s transaction pricing; it carries on in the background.');
  else if (a.complete === false) lines.push('It stopped at its time limit; the Core carries on in the background.');

  if (moves.length) {
    return { type: failures.length ? 'warning' : 'success', title: 'Moved from ' + label(a.from) + ' to ' + label(a.to) + '.', lines };
  }
  return { type: failures.length ? 'error' : 'info', title: 'Nothing moved: the day stays at ' + label(a.to ?? a.from) + '.', lines };
}

/** A move's actor: the person's name (null for an externally safe reader), a system's name, or the raw reference. */
export function actorText(event) {
  if (event?.actorName) return event.actorName;
  const actor = String(event?.actor ?? '');
  const system = /^SYSTEM:(.+)$/.exec(actor);
  if (system) return 'System (' + system[1] + ')';
  const person = /^CONSOLE:(.+)$/.exec(actor);
  return person ? person[1] : actor || '—';
}

/** The names the day's events give the people in them: uid -> name. */
export function namesOf(day) {
  const names = new Map();
  for (const e of day?.events ?? []) {
    const person = /^CONSOLE:(.+)$/.exec(String(e.actor ?? ''));
    if (person && e.actorName) names.set(person[1].toLowerCase(), e.actorName);
  }
  return names;
}

/**
 * The stage timeline, oldest first: when, what moved it (the reason in
 * words), from and to, what it passed over, and who - a person by name.
 *
 * @returns {Array<{ key: number, when: string, reason: string, move: string, passedOver: string|null, actor: string,
 *   forced: boolean, closed: boolean }>}
 */
export function eventItems(day, stages = stagesOf(null)) {
  return [...(day?.events ?? [])].sort((a, b) => a.eventId - b.eventId).map((e) => ({
    key: e.eventId,
    when: formatInstant(e.occurredAt),
    reason: REASON_TEXT[e.reason] ?? e.reason,
    move: (e.fromStage ? stageLabel(e.fromStage, stages) + ' → ' : '') + stageLabel(e.toStage, stages),
    passedOver: e.skipped?.length ? 'Passed over ' + e.skipped.map((s) => stageLabel(s, stages)).join(', ') : null,
    actor: actorText(e),
    forced: /_FORCED$/.test(String(e.reason ?? '')),
    closed: e.toStage === STAGE.CLOSED,
  }));
}

/** The day's forced moves (business_day.forced): the stage, when, who by name, why, and what was missing - the statements by account. */
export function forcedItems(day, stages = stagesOf(null)) {
  const names = namesOf(day);
  const accounts = accountNames(day);
  return (day?.forced ?? []).map((f, i) => ({
    key: f.stage + ':' + i,
    stage: stageLabel(f.stage, stages),
    when: formatInstant(f.at),
    by: names.get(String(f.by ?? '').toLowerCase()) ?? f.by ?? '—',
    reason: f.reason ?? '',
    missing: missingText(f.missing, day.businessDate, accounts).join('; '),
  }));
}

// ---------------------------------------------------------------------------
// The day-end analytics feeds tab (design 9; GET /books/feeds and its date)
// ---------------------------------------------------------------------------

/** The three feeds in the order the tab shows them, with their words. */
const FEED_LABEL = Object.freeze({ 'daily-transactions': 'Transactions', 'daily-unit-price': 'Unit price', 'daily-client-holdings': 'Client holdings' });
const FEED_ORDER = Object.keys(FEED_LABEL);
const feedLabel = (feed) => FEED_LABEL[feed] ?? feed;

/** Why a later date waits on an earlier one (feed_day.blocked_by.blocker: the earlier date's state, or a gap in the calendar). */
const BLOCKER_WORDS = Object.freeze({
  FAILED: 'failed',
  WAITING_FOR_WM: 'waiting for the wealth-managed holdings file',
  WAITING_EARLIER: 'waiting for an earlier date',
  NOT_CLOSED: 'not closed yet',
  MISSING_BUSINESS_DAY: 'not a business day the Core holds',
});

/**
 * The reason line of a date's feed state (design 9): the blocker and the
 * blocking date, the failure's code and feed, or why a sent date is stale.
 * '' for no date. A failure's message is there only for a reader with the
 * register claim (the server drops it otherwise).
 *
 * @param {{ state: string, blockedBy?: object|null, failure?: object|null }|null} day a feed_day as GET /books/feeds answers
 */
export function feedStateWords(day) {
  if (!day) return '';
  switch (day.state) {
    case 'PENDING': return 'Waiting to be built';
    case 'BUILDING': return 'Being built';
    case 'DELIVERING': return 'Being sent';
    case 'DONE': return 'Sent to analytics';
    case 'STALE': return 'Postings dated on or before this day arrived after its files were sent';
    case 'WAITING_FOR_WM': return 'Waiting for the wealth-managed holdings file, or a confirmation that there are none';
    case 'WAITING_EARLIER': {
      const by = day.blockedBy;
      if (!by?.businessDate) return 'Waiting for an earlier date';
      const why = BLOCKER_WORDS[by.blocker];
      return 'Waiting for ' + dayText(by.businessDate) + (why ? ', which is ' + why : '');
    }
    case 'FAILED': {
      const f = day.failure;
      if (!f) return 'Failed';
      const where = f.feed ? ' in the ' + feedLabel(f.feed).toLowerCase() + ' file' : '';
      return 'Failed' + where + (f.code ? ' (' + f.code + ')' : '') + (f.message ? ': ' + f.message : '');
    }
    default: return String(day.state ?? '');
  }
}

/** A feed_file as the tab shows it. */
function feedRow(file) {
  return {
    feed: file.feed,
    label: feedLabel(file.feed),
    version: file.version,
    mode: file.mode,
    state: file.state,
    rowCount: file.rowCount ?? null,
    fileName: file.fileName ?? null,
    sha256: file.sha256 ?? null,
    sentAt: file.sentAt ?? null,
    pickedUpAt: file.pickedUpAt ?? null,
    error: file.error ?? null,
    findings: file.findings ?? [],
    analytics: file.analytics ?? null,
    // EMPTY and OFF files, and a FAILED build, put nothing in the archive.
    downloadable: ['BUILT', 'SENT', 'PICKED_UP'].includes(file.state) && Boolean(file.fileName),
  };
}

const byFeedOrder = (a, b) => FEED_ORDER.indexOf(a.feed) - FEED_ORDER.indexOf(b.feed);

/** The current version's files of GET /books/days/{date}/feeds, one row per feed. */
export function feedRows(detail) {
  const current = detail?.feedDay?.version;
  return (detail?.files ?? []).filter((f) => f.version === current).map(feedRow).sort(byFeedOrder);
}

/** The older versions of that read, newest first, each with its files. */
export function feedVersions(detail) {
  const current = detail?.feedDay?.version;
  const byVersion = new Map();
  for (const file of detail?.files ?? []) {
    if (file.version === current) continue;
    byVersion.set(file.version, [...(byVersion.get(file.version) ?? []), feedRow(file)]);
  }
  return [...byVersion].sort(([a], [b]) => b - a).map(([version, rows]) => ({ version, rows: rows.sort(byFeedOrder) }));
}

// ---------------------------------------------------------------------------
// Analytics' reports on the feed files (IP-163): what the page shows of them
// ---------------------------------------------------------------------------

const ANALYTICS_DAY = Object.freeze({
  COMPLETED: { label: 'Completed', colour: 'green' },
  COMPLETED_WITH_ERRORS: { label: 'Completed with errors', colour: 'orange' },
  RUNNING: { label: 'Running', colour: 'blue' },
  WAITING: { label: 'Waiting', colour: 'gold' },
  AWAITING: { label: 'Awaiting analytics', colour: 'default' },
  FAILED: { label: 'Failed', colour: 'red' },
  REJECTED: { label: 'Rejected', colour: 'red' },
  UNAVAILABLE: { label: 'No analytics reports in this environment', colour: 'default' },
});

/** A date's analytics state (GET /books/feeds day.analytics): label, tag colour and "n of m runs finished"; null before a file is sent. */
export function analyticsDayWords(analytics) {
  if (!analytics) return null;
  const words = ANALYTICS_DAY[analytics.state] ?? { label: String(analytics.state), colour: 'default' };
  const detail = analytics.runs ? analytics.finished + ' of ' + analytics.runs + ' run' + (analytics.runs === 1 ? '' : 's') + ' finished' : '';
  return { ...words, detail };
}

const FILE_ANALYTICS = Object.freeze({
  ACCEPTED: { label: 'Accepted', colour: 'green' },
  REJECTED: { label: 'Rejected', colour: 'red' },
  AWAITING: { label: 'Awaiting', colour: 'default' },
});
const ACCEPTED_WITH_ERRORS = Object.freeze({ label: 'Accepted with errors', colour: 'orange' });

/**
 * What analytics did with one file: Accepted, Rejected or Awaiting - and
 * Accepted with errors when a run it should have started could not start
 * (R24); null for a file never sent.
 */
export function fileAnalyticsWords(analytics) {
  if (!analytics) return null;
  if (analytics.status === 'ACCEPTED' && analytics.error) return ACCEPTED_WITH_ERRORS;
  return FILE_ANALYTICS[analytics.status] ?? { label: String(analytics.status), colour: 'default' };
}

const RUN_TITLE = Object.freeze({
  lyn_holdings_output: 'Holdings output', 'lyn-fund-returns': 'Fund returns', 'lyn-security-allocation': 'Security allocation',
});

/** A run's title: sync transactions started by a file, or the returns a holdings run chains. */
export function runTitle(run) {
  if (run.stateMachine === 'lyn_sync_transactions') return run.trigger === 'CHAIN' ? 'Returns' : 'Sync transactions';
  return RUN_TITLE[run.stateMachine] ?? run.stateMachine;
}

const RUN_OUTCOME = Object.freeze({
  COMPLETED: { label: 'Completed', colour: 'green' },
  COMPLETED_WITH_ERRORS: { label: 'Completed with errors', colour: 'orange' },
  WAITING: { label: 'Waiting', colour: 'gold' },
  FAILED: { label: 'Failed', colour: 'red' },
});

/** A run's state tag: Running while it runs, then its outcome (an ended run with no outcome is Failed). */
export function runState(run) {
  if (run.status === 'RUNNING') return { label: 'Running', colour: 'blue' };
  if (run.status !== 'SUCCEEDED') return RUN_OUTCOME.FAILED;
  return RUN_OUTCOME[run.outcome] ?? RUN_OUTCOME.COMPLETED;
}

/**
 * Why a run failed, in one line (M4): its error name, and ': ' + cause when
 * there is one - a reader without the register claim gets the name only, the
 * API having cut the cause (AUTH-1). null when the run has no error.
 */
export const runErrorText = (error) => (error ? error.name + (error.cause ? ': ' + error.cause : '') : null);

/** How long an ended run took: "9 s", "3 min 1 s"; '' while it runs. */
export function runDuration(run) {
  if (!run.stoppedAt) return '';
  const seconds = Math.max(0, Math.round((Date.parse(run.stoppedAt) - Date.parse(run.startedAt)) / 1000));
  return seconds < 60 ? seconds + ' s' : Math.floor(seconds / 60) + ' min ' + (seconds % 60) + ' s';
}

const STEP_LABEL = Object.freeze({
  SyncToRDS: 'Update account summaries', SyncReturnsToRDS: 'Update returns', CheckSForFU: 'Check the trigger', CheckSfOrFu: 'Check the trigger',
  WaitForBothFiles: 'Waiting for both files', WaitForInputs: 'Waiting for inputs', SkipNoWork: 'Nothing to compute',
});

/** A state name in words: "ComputeHoldingsOutput" -> "Compute holdings output". */
export function stepLabel(name) {
  if (STEP_LABEL[name]) return STEP_LABEL[name];
  const words = String(name).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const SHOWN = Object.freeze(new Set(['Task', 'Map', 'Wait', 'Parallel']));
const STEP_STATUS = Object.freeze({ SUCCEEDED: 'finish', FAILED: 'error', RUNNING: 'process' });
const RUN_ENDED_BADLY = Object.freeze(new Set(['FAILED', 'TIMED_OUT', 'ABORTED']));

/**
 * One step's antd status. A step still RUNNING inside a run that has ended
 * (M3: the run timed out or was aborted mid-step) is never 'process': it is
 * an error when the run failed, otherwise it waits.
 */
function stepStatus(step, run) {
  if (step.status === 'RUNNING' && run.status !== 'RUNNING') {
    return run.outcome === 'FAILED' || RUN_ENDED_BADLY.has(run.status) ? 'error' : 'wait';
  }
  return STEP_STATUS[step.status] ?? 'wait';
}

/**
 * The steps the page shows of a run, as antd Steps items: the work (Task,
 * Map, Wait, Parallel) and a WaitFor... or SkipNoWork end; never a Choice, a
 * Pass, or the success and fail ends. A run still running reports no steps
 * yet (IP-161): one "Running…" step.
 */
export function visibleSteps(run) {
  if (run.status === 'RUNNING' && !(run.steps ?? []).length) return [{ key: 'running', title: 'Running…', status: 'process', description: null }];
  return (run.steps ?? [])
    .filter((s) => SHOWN.has(s.type) || (s.type === 'Succeed' && (s.name.startsWith('WaitFor') || s.name === 'SkipNoWork')))
    .map((s) => ({
      key: s.name,
      title: stepLabel(s.name) + (s.entries > 1 ? ' ×' + s.entries : ''),
      status: s.type === 'Succeed' ? (s.name.startsWith('WaitFor') ? 'wait' : 'finish') : stepStatus(s, run),
      description: s.error ? s.error.name : null,
    }));
}

/** The runs a version's files chained (holdings output → returns → security allocation). */
export const chainedRunsOf = (detail, version) => (detail?.chainedRuns ?? []).find((v) => v.version === version)?.runs ?? [];

const RESENDABLE = Object.freeze(['DONE', 'STALE', 'FAILED']);
const WORKING = Object.freeze(['BUILDING', 'DELIVERING']);

/**
 * Whether a date can be sent again (FEEDS_RESEND): DONE, STALE or FAILED - or
 * BUILDING or DELIVERING, which the server takes only when the run that left
 * it is dead (no live lease) and refuses FEEDS_BUSY while it is alive (I1).
 */
export const canResendDate = (day) => Boolean(day) && (RESENDABLE.includes(day.state) || WORKING.includes(day.state));

/** Whether a date can be told it has no wealth-managed holdings (FEEDS_WM_WAIVE: it is waiting for them). */
export const canWaiveWmDate = (day) => day?.state === 'WAITING_FOR_WM';

/**
 * The dates a re-send of `businessDate` sets PENDING: that date and every later
 * DONE, STALE or FAILED one (actions.js feedsResendPlan), oldest first; none
 * when the date itself cannot be sent again or the list does not hold it.
 *
 * @param {Array<{ businessDate: string, state: string }>|null} days
 */
export function resendDates(days, businessDate) {
  const day = (days ?? []).find((d) => d.businessDate === businessDate);
  if (!canResendDate(day)) return [];
  return days.filter((d) => d.businessDate === businessDate || (d.businessDate > businessDate && RESENDABLE.includes(d.state)))
    .map((d) => d.businessDate).sort();
}

/**
 * Whether a feed's archived file may be downloaded by these claims: the unit
 * price file by any view claim, the transactions and holdings (client data)
 * only with the register's claim - as the file route checks them.
 */
export function canDownload(feed, claims) {
  const held = claims ?? [];
  if (feed === 'daily-unit-price') return held.some((c) => VIEW_CLAIMS.includes(c));
  return held.includes('core.registry.view');
}

// ---------------------------------------------------------------------------
// Day end, run from the Console (IP-195, IP-200; GET /books/days's dayEnd)
// ---------------------------------------------------------------------------

/** Whether the Day end process owns the day: GET sends its dayEnd (a day before dayEndStartDate has none, and closes the old way). */
export const isDayEndDay = (day) => Boolean(day?.dayEnd);

/**
 * The earliest day before this one, in the view, that the Day end process
 * owns and is not CLOSED - days close in order - or null. The server decides
 * again: a day the view does not hold (hiddenDaysText), or never made, counts
 * there too.
 */
export function earlierOpenDay(view, day) {
  const earlier = liveDays(view).filter((d) => isDayEndDay(d) && d.businessDate < day.businessDate);
  return earlier.length ? earlier[0].businessDate : null;
}

/** Each status in words, with its tag's colour (day-end/reads.js DAY_END_STATUS). */
export const DAY_END_WORDS = Object.freeze({
  NOT_STARTED: { label: 'Not run yet', colour: 'default' },
  RUNNING: { label: 'Running', colour: 'blue' },
  STOPPED: { label: 'Stopped with issues', colour: 'red' },
  FAILED: { label: 'Failed', colour: 'red' },
  NOT_READY: { label: 'Did not start', colour: 'gold' },
  CLOSED: { label: 'Closed', colour: 'green' },
  UNKNOWN: { label: 'Status unknown', colour: 'default' },
});

/** From this stage on, a Day end day shows its Day End panel and Run day end, and Advance steps aside. */
const fromReconciliation = (day, stages) => rankIn(stages, day?.stage) >= rankIn(stages, STAGE.RECONCILING);

/**
 * Whether Advance is offered for the day: always, but on a day the Day end
 * process owns only before Reconciliation & approvals (IP-200) - there it
 * would move nothing, and Run day end closes the day.
 */
export function advanceShown(day, stages = stagesOf(null)) {
  return !isDayEndDay(day) || !fromReconciliation(day, stages);
}

/** The line under Advance: on a Day end day it prices, and Run day end takes the day on from Reconciliation; on any other day, as before. */
export function advanceHint(day) {
  if (!isDayEndDay(day)) {
    return 'Advance runs the day\'s next steps now - pricing and day end, as far as its facts allow - as the Core does by itself.';
  }
  return 'Advance runs the day\'s next steps now - pricing, as far as its facts allow. From Reconciliation & approvals, Run day end closes the day.';
}

/**
 * The Run day end button (DAY_END_RUN, IP-195): shown on a day the Day end
 * process owns from Reconciliation & approvals until it closes; "Run day end
 * again" after a run that stopped or failed; held - with why - while a run is
 * going or an earlier day is still open (the server refuses both too).
 *
 * @param {object} day one of GET /books/days's days, with its dayEnd
 * @param {{ view?: object|null, stages?: object[] }} [at]
 * @returns {{ show: boolean, label: string, enabled: boolean, reason: string|null }}
 */
export function runDayEndState(day, { view = null, stages = stagesOf(view) } = {}) {
  const show = isDayEndDay(day) && fromReconciliation(day, stages) && day.stage !== STAGE.CLOSED;
  const status = day?.dayEnd?.status ?? null;
  const label = status === 'STOPPED' || status === 'FAILED' ? 'Run day end again' : 'Run day end';
  if (!show) return { show, label, enabled: false, reason: null };
  if (status === 'RUNNING') return { show, label, enabled: false, reason: 'Day end is running for this day.' };
  const earlier = view ? earlierOpenDay(view, day) : null;
  if (earlier) return { show, label, enabled: false, reason: 'Close ' + dayShort(earlier) + ' first: days close in order.' };
  return { show, label, enabled: true, reason: null };
}

/** The line under Run day end. */
export const RUN_DAY_END_HINT = 'Run day end carries over the reconciled transactions still waiting for approval and moves the rest to the next'
  + ' dealing date, then checks every holder\'s units and closes the day - in the background. The code dialog lists them first.';

/**
 * IP-203 (the owner, 19 Oct 2026): what the drawer says beside Run day end about the day's
 * transactions not yet approved, from GET books/days' dayEnd.warnings (domain day-end/carry.js
 * dayEndWarnings) - so the person knows before they press it.
 *
 *   confirm   reconciled transactions not yet approved: Run day end asks first. They move on to
 *             the next day but keep the day's price until approved.
 *   info      reviewed (accepted) transactions not yet reconciled: an "i" beside the button. Day
 *             end moves them to the next dealing date, priced with the new unit prices. Only when
 *             there are some.
 *
 * @param {{ businessDate: string, dayEnd?: { warnings?: { reconciledNotApproved: number, reviewedNotReconciled: number }|null } }} day
 * @returns {{ confirm: string|null, info: string|null }}
 */
export function dayEndWarningsText(day) {
  const w = day?.dayEnd?.warnings ?? null;
  const reconciled = Number(w?.reconciledNotApproved ?? 0);
  const reviewed = Number(w?.reviewedNotReconciled ?? 0);
  const many = (n, one, more) => n + ' ' + (n === 1 ? one : more);
  return {
    confirm: reconciled > 0
      ? many(reconciled, 'reconciled transaction is', 'reconciled transactions are') + ' not approved yet. You can still run day end: '
        + (reconciled === 1 ? 'it moves' : 'they move') + ' on to the next day and ' + (reconciled === 1 ? 'keeps ' : 'keep ')
        + dayText(day.businessDate) + '\'s price until approved.'
      : null,
    info: reviewed > 0
      ? many(reviewed, 'reviewed transaction is', 'reviewed transactions are') + ' not reconciled yet. If you run day end now, '
        + (reviewed === 1 ? 'it moves' : 'they move') + ' to the next dealing date and ' + (reviewed === 1 ? 'is' : 'are')
        + ' priced with the new unit prices.'
      : null,
  };
}

/** What the drawer says once Run day end has started a run (console-api's answer: businessDate, execution). */
export function runDayEndOutcome(answer) {
  return {
    type: 'success',
    title: 'Day end started for ' + dayText(answer?.businessDate) + '.',
    lines: [
      'It runs in the background, usually for a few minutes: the Day End panel shows its progress.',
      ...(answer?.execution ? ['Run ID: ' + answer.execution] : []),
    ],
  };
}

/**
 * Run day end refused, in words: an earlier day open and a run already going
 * are the server's own checks (IP-200), said as the drawer says them; any
 * other refusal is the server's message.
 *
 * @param {{ code?: string, message?: string, details?: object }} error console's ApiError
 */
export function runDayEndRefusal(error, businessDate) {
  const details = error?.details ?? {};
  if (error?.code === 'DAY_END_EARLIER_DAY_OPEN' && details.earlierOpen) {
    return { type: 'warning', title: 'Close ' + dayText(details.earlierOpen) + ' first: days close in order.', lines: [] };
  }
  if (error?.code === 'DAY_END_RUNNING') {
    return { type: 'info', title: 'Day end is already running for ' + dayText(businessDate) + '.', lines: details.execution ? ['Run ID: ' + details.execution] : [] };
  }
  return { type: 'error', title: error?.message ?? 'Day end could not be started.', lines: [] };
}

/**
 * The Day End panel of a day the Day end process owns, from Reconciliation &
 * approvals on (show): its status as a tag and lines, and the latest
 * attempt's progress while it runs.
 *
 * @param {object} day one of GET /books/days's days, with its dayEnd
 * @param {object[]} [stages]
 * @returns {{ show: boolean, status: string, tag: { label: string, colour: string }, lines: string[],
 *   progress: { done: number, total: number, percent: number }|null }}
 */
export function dayEndPanel(day, stages = stagesOf(null)) {
  const show = isDayEndDay(day) && fromReconciliation(day, stages);
  const d = day?.dayEnd ?? {};
  const status = d.status ?? 'UNKNOWN';
  const run = d.lastRun ?? null;
  const by = run?.startedByName ?? null;
  const started = run?.startedAt ? 'Started ' + whenText(run.startedAt, day.businessDate) + (by ? ' by ' + by : '') + '.' : null;
  const lines = [];
  let progress = null;

  switch (status) {
    case 'NOT_STARTED':
      lines.push('Run day end decides the transactions still waiting for approval, checks every holder\'s units as at this day, saves the day\'s'
        + ' balances and closes the day.');
      break;
    case 'RUNNING': {
      if (started) lines.push(started);
      const a = d.attempt;
      if (a && a.runId === run?.runId && a.chunks.total > 0) {
        progress = { done: a.chunks.done, total: a.chunks.total, percent: Math.round((100 * a.chunks.done) / a.chunks.total) };
        lines.push('Checking the holders: ' + a.chunks.done + ' of ' + plural(a.chunks.total, 'part') + ' done.');
      } else {
        lines.push('Getting ready to check the holders.');
      }
      break;
    }
    case 'STOPPED':
      lines.push('Day end found ' + plural(Number(d.openIssues ?? 0), 'issue') + ' in the holders\' units and stopped. The day stays in Day end,'
        + ' and the funds with issues cannot approve their next price.');
      lines.push('Raise a ticket to engineering with the run ID. Once they have fixed the data, press Run day end again.');
      if (started) lines.push(started);
      break;
    case 'FAILED':
      lines.push('The last run stopped on a technical error. Nothing is lost: press Run day end again.');
      if (started) lines.push(started);
      break;
    case 'NOT_READY':
      lines.push('The last run ended without starting Day end: an earlier day was still open, or a transaction was still to be frozen.'
        + ' Then press Run day end again.');
      if (started) lines.push(started);
      break;
    case 'CLOSED':
      lines.push('Closed by Day end' + (day.closedAt ? ' ' + whenText(day.closedAt, day.businessDate) : '') + '.' + (by ? ' Started by ' + by + '.' : ''));
      break;
    default:
      lines.push('Day end\'s state could not be read just now. Refresh to try again.');
  }
  if (run?.runId && status !== 'NOT_STARTED' && status !== 'CLOSED') lines.push('Run ID: ' + run.runId);
  return { show, status, tag: DAY_END_WORDS[status] ?? DAY_END_WORDS.UNKNOWN, lines, progress };
}
